import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { createLobby } from "src/store/lobby"
import { selectGameModes, selectLobbyPasswordLimit, selectMapSizes } from "src/store/common"
import { gameModeName } from "src/utils/gameModes"
import { roundLengthLabel } from "src/utils/duration"
import { ROUND_LENGTH_OPTIONS } from "src/utils/rounds"
import { mapSizeOptionLabel } from "src/utils/mapSizes"

import Modal from "src/components/modals/Modal"
import { apiErrorLines } from "src/utils/apiErrors"

const CreateLobbyModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  /* The list comes from `GET /game-config/` rather than being typed out here (survival was the
     fourth, 2026-09-16): three literals were three chances to offer a mode this server refuses,
     and one more each time the enum grows. `gameModeName` is what turns a published string into
     something a player reads — "1v1" is already that, "survival" is not. */
  const gameModes = useSelector(selectGameModes)
  // The map sizes, the server's own rows (requirement 35, M11): each worded by its crowns. Empty
  // on a server that has only the one world, and then the dialog asks nothing about it.
  const mapSizes = useSelector(selectMapSizes)
  // Where the server's column ends: typed past it, the room was refused with a 500 (Sentry).
  const passwordLimit = useSelector(selectLobbyPasswordLimit)

  const [errors, setErrors] = useState([])
  const [formData, setFormData] = useState({
    // Pre-selected at 1v1 (owner, 2026-10-09).
    mode: "1v1",
    // Pre-selected at two minutes (owner, 2026-10-08).
    roundSeconds: 120,
    // Whether the game moves the ranking (owner, 2026-10-04). The server's default is ranked.
    ranked: "ranked",
    // The world it is played on (requirement 35, M12): the small one pre-selected (owner,
    // 2026-10-08).
    mapSize: "S",
    // Public or private (owner, 2026-10-07): a bare password field read as one more thing to fill
    // in, so the password is asked only once the player says the room is private.
    visibility: "public",
    password: "",
  })
  const handleSubmit = (e) => {
    e.preventDefault()
    dispatch(
      createLobby({
        mode: formData.mode,
        round_seconds: Number(formData.roundSeconds) || 120,
        ranked: formData.ranked !== "unranked",
        ...(mapSizes.length ? { map_size: formData.mapSize } : {}),
        password: formData.visibility === "private" ? formData.password || null : null,
      })
    )
      .unwrap()
      .then(() => ModalService.hideAll())
      .catch(setErrors)
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  return (
    <Modal>
      <h1>{t("lobbyModals.createTitle")}</h1>
      {apiErrorLines(errors).map(({ key, text }) => (
        <p key={key} className="errored-text">
          <br />
          {text}
        </p>
      ))}
      <br />
      <br />
      <form onSubmit={handleSubmit}>
        <label htmlFor="mode">{t("lobbyModals.modeLabel")}</label>
        <select
          id="mode"
          name="mode"
          className="form-select"
          value={formData.mode}
          onChange={handleInputChange}
          required
        >
          <option value="" disabled>
            {t("lobbyModals.selectMode")}
          </option>
          {gameModes.map((mode) => (
            <option key={mode} value={mode}>
              {gameModeName(mode)}
            </option>
          ))}
        </select>

        <label htmlFor="roundSeconds">{t("lobbyModals.roundLengthLabel")}</label>
        <select
          id="roundSeconds"
          name="roundSeconds"
          className="form-select"
          value={formData.roundSeconds}
          onChange={handleInputChange}
        >
          {ROUND_LENGTH_OPTIONS.map((seconds) => (
            <option key={seconds} value={seconds}>
              {roundLengthLabel(seconds, t)}
            </option>
          ))}
        </select>

        <label htmlFor="ranked">{t("lobbyModals.rankedLabel")}</label>
        <select id="ranked" name="ranked" className="form-select" value={formData.ranked} onChange={handleInputChange}>
          <option value="ranked">{t("lobbyModals.ranked")}</option>
          <option value="unranked">{t("lobbyModals.unranked")}</option>
        </select>

        {mapSizes.length > 0 && (
          <>
            <label htmlFor="mapSize">{t("lobby.mapSize.label")}</label>
            <select id="mapSize" name="mapSize" className="form-select" value={formData.mapSize} onChange={handleInputChange}>
              {mapSizes.map((row) => (
                <option key={row.size} value={row.size}>
                  {mapSizeOptionLabel(row)}
                </option>
              ))}
            </select>
          </>
        )}

        <label htmlFor="visibility">{t("lobbyModals.visibilityLabel")}</label>
        <select
          id="visibility"
          name="visibility"
          className="form-select"
          value={formData.visibility}
          onChange={handleInputChange}
        >
          <option value="public">{t("lobbyModals.public")}</option>
          <option value="private">{t("lobbyModals.private")}</option>
        </select>

        {formData.visibility === "private" && (
          <>
            <label htmlFor="password">{t("lobbyModals.passwordLabel")}</label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              placeholder={t("lobbyModals.createPasswordPlaceholder")}
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              maxLength={passwordLimit ?? undefined}
              required
            />
          </>
        )}

        <br />
        <br />
        <button type="submit">{t("lobbyModals.createButton")}</button>
      </form>
    </Modal>
  )
}

export default CreateLobbyModal
